import { Component, inject, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, HostListener } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TimeEntriesService } from '../../../shared/services/time-entries.service';
import { UserService } from '../../../shared/services/user.service';
import { ProjectService } from '../../../shared/services/project.service';
import { TimeEntry } from '../../../shared/models/activity.model';
import { User } from '../../../shared/models/user.model';
import { EntryFilter } from '../../../shared/models/project.model';

@Component({
    selector: 'app-time-entry-approval',
    imports: [CommonModule, FormsModule],
    providers: [DatePipe],
    templateUrl: './time-entry-approval.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeEntryApprovalComponent implements OnInit {
    private timeEntryService = inject(TimeEntriesService);
    private userService = inject(UserService);
    private projectService = inject(ProjectService);
    private datePipe = inject(DatePipe);
    private cdr = inject(ChangeDetectorRef);
    private elRef = inject(ElementRef);

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (this.projectDropdownOpen && !this.elRef.nativeElement.querySelector('.project-filter-container')?.contains(event.target)) {
            this.projectDropdownOpen = false;
            this.cdr.markForCheck();
        }
    }

    entries: TimeEntry[] = [];
    employees: User[] = [];
    /** Archived employees are hidden from the employee filter by default. */
    showArchivedEmployees = false;

    /**
     * Employees offered in the filter: active ones, plus archived ones when the
     * admin ticks the box, plus whoever is currently selected.
     * `employees` itself keeps everybody so archived names still resolve.
     */
    get visibleEmployees(): User[] {
      return this.employees.filter(e =>
        !e.isArchived ||
        this.showArchivedEmployees ||
        (!!this.selectedEmployeeId && e.cognitoId === this.selectedEmployeeId)
      );
    }

    get archivedEmployeeCount(): number {
      return this.employees.filter(e => !!e.isArchived).length;
    }

    loading = true;
    processingId: string | null = null;

    // Filters
    selectedEmployeeId = '';
    statusFilter = 'DRAFT'; // default to draft for quick action

    // Multi-select project filter — keys are projectId UUIDs, "[Task] ..." keys, or legacy base names.
    selectedProjects: Set<string> = new Set();
    projectSearch = '';
    projectDropdownOpen = false;

    /** Dropdown keys discovered from loaded entries (UUID / "[Task] ..." / legacy base name). */
    allKnownProjectKeys: Set<string> = new Set();
    /** Resolves UUID keys → current project name for display (kept in sync with the live project list). */
    projectIdToName: Map<string, string> = new Map();

    ngOnInit() {
        this.userService.users$.subscribe(users => {
            this.employees = users;
            this.cdr.markForCheck();
        });
        // Keep UUID → name resolution up to date with the live project list (rename-proof)
        this.projectService.projects$.subscribe(projects => {
            projects.forEach(p => {
                if (p.projectId) this.projectIdToName.set(p.projectId, p.projectName);
            });
            this.cdr.markForCheck();
        });
        this.loadEntries();
    }

    get filteredEntries(): TimeEntry[] {
        return this.entries.filter(e => {
            const statusMatch = !this.statusFilter || (e.approvalStatus ?? 'DRAFT') === this.statusFilter;
            const empMatch = !this.selectedEmployeeId || e.employeeId === this.selectedEmployeeId;
            const projectMatch = this.selectedProjects.size === 0 || this.selectedProjects.has(this.getBaseProjectKey(e));
            return statusMatch && empMatch && projectMatch;
        });
    }

    /** Extracts the dropdown key for an entry:
     *  - CRM task                → "[Task] ..."
     *  - Entry with projectId    → the projectId UUID (rename-proof)
     *  - Legacy (no projectId)   → base project name string
     */
    getBaseProjectKey(entry: { projectName?: string | null; projectId?: string | null }): string {
        const name = entry.projectName ?? '';
        if (name.startsWith('[Task] ')) return name;          // CRM task
        if (entry.projectId)            return entry.projectId; // UUID key
        return name.split(' - ')[0].trim();                   // Legacy fallback
    }

    /** Resolves a dropdown key to its display name. */
    resolveKeyName(key: string): string {
        if (this.projectIdToName.has(key)) return this.projectIdToName.get(key)!;
        if (key.startsWith('[Task] '))    return key.replace(/^\[Task\] /, '');
        return key;
    }

    /** Unique project keys present in the loaded entries, sorted by display name. */
    get uniqueProjectKeys(): string[] {
        return [...this.allKnownProjectKeys].sort((a, b) =>
            this.resolveKeyName(a).localeCompare(this.resolveKeyName(b))
        );
    }

    /** Project keys filtered by the search input (matches display name). */
    get filteredProjectOptions(): string[] {
        const q = this.projectSearch.trim().toLowerCase();
        return q
            ? this.uniqueProjectKeys.filter(k => this.resolveKeyName(k).toLowerCase().includes(q))
            : this.uniqueProjectKeys;
    }

    /** Toggling a project re-queries the backend with the new project filter. */
    toggleProject(key: string) {
        const next = new Set(this.selectedProjects);
        if (next.has(key)) next.delete(key); else next.add(key);
        this.selectedProjects = next;
        this.loadEntries(false); // keep the options list; only re-fetch the filtered table
    }

    clearProjectFilter() {
        this.selectedProjects = new Set();
        this.projectSearch = '';
        this.loadEntries(false);
    }

    toggleProjectDropdown() {
        this.projectDropdownOpen = !this.projectDropdownOpen;
        this.cdr.markForCheck();
    }

    get draftCount(): number {
        return this.entries.filter(e => (e.approvalStatus ?? 'DRAFT') === 'DRAFT').length;
    }

    /** Changing employee re-scopes the project options, so rebuild them. */
    async onFilterChange() {
        await this.loadEntries(true);
    }

    /** Builds the server-side filter, splitting project keys into projectIds / legacy names / CRM tasks. */
    private buildFilters(): EntryFilter {
        const filters: EntryFilter = {};
        if (this.selectedEmployeeId) filters.employeeId = this.selectedEmployeeId;

        if (this.selectedProjects.size > 0) {
            const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            const keys = [...this.selectedProjects];
            const projectIds = keys.filter(k => uuidPattern.test(k));
            const crmKeys    = keys.filter(k => k.startsWith('[Task] '));
            const nameKeys   = keys.filter(k => !uuidPattern.test(k) && !k.startsWith('[Task] '));
            if (projectIds.length) filters.projectIds       = projectIds;
            if (nameKeys.length)   filters.baseProjectNames = nameKeys;
            if (crmKeys.length)    filters.crmTaskNames     = crmKeys;
        }
        return filters;
    }

    /** Rebuilds the project dropdown options from an unfiltered (by project) entry set. */
    private rebuildProjectOptions(entries: TimeEntry[]) {
        const keys = new Set<string>();
        entries.forEach(e => {
            const key = this.getBaseProjectKey(e);
            if (key) keys.add(key);
            // Seed a display name for UUID keys (the projects$ subscription keeps these current)
            if (e.projectId && !this.projectIdToName.has(e.projectId) && !e.projectName?.startsWith('[Task] ')) {
                const baseName = (e.projectName ?? '').split(' - ')[0].trim();
                if (baseName) this.projectIdToName.set(e.projectId, baseName);
            }
        });
        this.allKnownProjectKeys = keys;
        // Drop any selected projects that no longer exist in the current scope
        if (this.selectedProjects.size > 0) {
            this.selectedProjects = new Set([...this.selectedProjects].filter(k => keys.has(k)));
        }
    }

    /**
     * @param refreshOptions When true, also fetch the unfiltered (by project) set to rebuild
     *   the dropdown options — needed when the employee scope changes or on first load.
     */
    private async loadEntries(refreshOptions = true) {
        this.loading = true;
        this.cdr.markForCheck();
        try {
            // Base filter = everything except the project selection (scopes the option list)
            const baseFilters: EntryFilter = {};
            if (this.selectedEmployeeId) baseFilters.employeeId = this.selectedEmployeeId;

            let baseEntries: TimeEntry[] | null = null;
            if (refreshOptions) {
                // Fetch ALL pages (not just 50) so no entries/options are missed
                baseEntries = await this.timeEntryService.fetchAllEntries(baseFilters);
                this.rebuildProjectOptions(baseEntries);
            }

            if (this.selectedProjects.size === 0) {
                // No project filter → the table is the base set (reuse if we just fetched it)
                this.entries = baseEntries ?? await this.timeEntryService.fetchAllEntries(baseFilters);
            } else {
                // Server-side project filter applied via projectIds / baseProjectNames / crmTaskNames
                this.entries = await this.timeEntryService.fetchAllEntries(this.buildFilters());
            }
        } catch (e) {
            console.error('[Approval] Failed to load entries', e);
        } finally {
            this.loading = false;
            this.cdr.markForCheck();
        }
    }

    async toggleBillable(entry: TimeEntry) {
        const newVal = !(entry.billable ?? true);
        const result = await this.timeEntryService.updateTimeEntry(entry.id!, { billable: newVal });
        if (result !== null) {
            entry.billable = newVal;
        }
        this.cdr.markForCheck();
    }

    async approve(entry: TimeEntry) {
        this.processingId = entry.id!;
        this.cdr.markForCheck();
        const result = await this.timeEntryService.approveEntry(entry.id!);
        // Optimistic update — change local state immediately
        if (result !== null) {
            entry.approvalStatus = 'APPROVED';
        }
        this.processingId = null;
        this.cdr.markForCheck();
    }

    async setDraft(entry: TimeEntry) {
        this.processingId = entry.id!;
        this.cdr.markForCheck();
        const result = await this.timeEntryService.setDraftEntry(entry.id!);
        if (result !== null) {
            entry.approvalStatus = 'DRAFT';
        }
        this.processingId = null;
        this.cdr.markForCheck();
    }

    getEmployeeName(id: string | null | undefined): string {
        if (!id) return 'Unknown';
        const u = this.employees.find(e => e.cognitoId === id);
        return u ? `${u.firstName} ${u.name}` : 'Unknown';
    }

    getDuration(start: Date | string, end: Date | string): string {
        const diff = new Date(end).getTime() - new Date(start).getTime();
        const h = Math.floor(diff / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        return h < 1 ? `${m}m` : `${h}h ${m}m`;
    }

    getEntryName(projectName: string | null | undefined): string {
        const n = projectName ?? '';
        return n.startsWith('[Task] ') ? n.replace(/^\[Task\] /, '') : n;
    }

    getEntryType(projectName: string | null | undefined): 'CRM Task' | 'Project' {
        return (projectName ?? '').startsWith('[Task] ') ? 'CRM Task' : 'Project';
    }
}
